import React, { useState, useMemo } from "react";
import { Input } from "./input";
import { Button } from "./button";
import { Search, ChevronLeft, ChevronRight } from "lucide-react";

export interface DataTableColumn<T> {
  key: string;
  label: string;
  render?: (row: T) => React.ReactNode;
  className?: string;
  searchable?: boolean; // Se a coluna deve ser incluída na busca
}

interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  data: T[];
  loading?: boolean;
  emptyText?: string;
  searchable?: boolean; // Se deve mostrar campo de busca
  searchPlaceholder?: string;
  pagination?: boolean; // Se deve mostrar paginação
  pageSize?: number; // Itens por página
  showSearch?: boolean; // Alias para searchable (retrocompatibilidade)
  customFilters?: React.ReactNode; // Filtros customizados para aparecer ao lado do campo de busca
}

export function DataTable<T extends { id: string | number }>(props: DataTableProps<T>) {
  const { 
    columns, 
    data, 
    loading, 
    emptyText = "Nenhum registro encontrado.",
    searchable = true,
    searchPlaceholder = "Buscar...",
    pagination = true,
    pageSize = 10,
    showSearch,
    customFilters,
  } = props;

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // Determinar se deve mostrar busca (prioridade: showSearch > searchable)
  const shouldShowSearch = showSearch !== undefined ? showSearch : searchable;
  
  // Colunas que podem ser buscadas (padrão: todas exceto actions)
  const searchableColumns = columns.filter(col => 
    col.searchable !== false && col.key !== 'actions'
  );

  // Filtrar dados baseado na busca
  const filteredData = useMemo(() => {
    if (!shouldShowSearch || !searchTerm.trim()) {
      return data;
    }

    const term = searchTerm.toLowerCase();
    return data.filter((row) => {
      return searchableColumns.some((col) => {
        // Sempre prioriza o valor direto da propriedade para busca mais precisa
        const directValue = (row as any)[col.key];
        if (directValue !== undefined && directValue !== null) {
          const directValueStr = String(directValue).toLowerCase();
          if (directValueStr.includes(term)) {
            return true;
          }
        }
        
        // Se tem render customizado e não encontrou no valor direto,
        // tenta buscar em outras propriedades relacionadas (ex: description para name)
        if (col.render) {
          // Para a coluna "name", também busca na description se disponível
          if (col.key === 'name' && (row as any).description) {
            const descValue = String((row as any).description || '').toLowerCase();
            if (descValue.includes(term)) {
              return true;
            }
          }
        }
        
        return false;
      });
    });
  }, [data, searchTerm, shouldShowSearch, searchableColumns]);

  // Calcular paginação
  const totalPages = pagination ? Math.ceil(filteredData.length / pageSize) : 1;
  const startIndex = pagination ? (currentPage - 1) * pageSize : 0;
  const endIndex = pagination ? startIndex + pageSize : filteredData.length;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  // Resetar página quando busca mudar
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(totalPages, prev + 1));
  };

  return (
    <div className="space-y-4">
      {/* Campo de Busca e Filtros Customizados */}
      {(shouldShowSearch || customFilters) && (
        <div className="flex items-center gap-2 flex-wrap">
          {shouldShowSearch && (
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          )}
          {customFilters && (
            <div className="flex items-center gap-2">
              {customFilters}
            </div>
          )}
          {searchTerm && shouldShowSearch && (
            <span className="text-sm text-muted-foreground whitespace-nowrap">
              {filteredData.length} resultado{filteredData.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      )}

      <div className="overflow-x-auto">
        {/* Tabela tradicional para md+ */}
        <table className="w-full text-sm hidden sm:table table-fixed">
        <colgroup>
          {columns.map((col, idx) => (
            <col key={col.key} style={{ width: `${100 / columns.length}%` }} />
          ))}
        </colgroup>
        <thead className="border-b bg-muted/50">
          <tr>
            {columns.map((col, idx) => (
              <th
                key={col.key}
                className={`h-10 px-4 text-left align-middle font-medium ${col.className || ''} ${col.key === 'actions' ? 'text-right pl-6' : ''}`}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={columns.length} className="p-4 text-center">Carregando...</td></tr>
          ) : paginatedData.length === 0 ? (
            <tr><td colSpan={columns.length} className="p-4 text-center">{emptyText}</td></tr>
          ) : (
            paginatedData.map(row => (
              <tr key={row.id} className="border-b transition-colors hover:bg-muted/50">
                {columns.map((col, idx) => (
                  <td
                    key={`${row.id}-${col.key}-${idx}`}
                    className={`p-4 ${col.className || ''} ${col.key === 'actions' ? 'text-right' : ''}`}
                  >
                    {col.render ? col.render(row) : (row as any)[col.key]}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
        {/* Mobile: cartões empilhados */}
        <div className="sm:hidden flex flex-col gap-4">
          {loading ? (
            <div className="p-4 text-center">Carregando...</div>
          ) : paginatedData.length === 0 ? (
            <div className="p-4 text-center">{emptyText}</div>
          ) : (
            paginatedData.map(row => (
            <div key={row.id} className="rounded border p-3 flex flex-col gap-2 bg-white">
              {columns.filter(col => col.key !== 'actions').map(col => (
                <div key={col.key} className="flex justify-between text-sm">
                  <span className="font-medium text-muted-foreground mr-2">{col.label}:</span>
                  <span>{col.render ? col.render(row) : (row as any)[col.key]}</span>
                </div>
              ))}
              {/* Ações embaixo, centralizadas */}
              {columns.find(col => col.key === 'actions') && (
                <div className="flex gap-2 justify-end mt-2">
                  {columns.find(col => col.key === 'actions')!.render!(row)}
                </div>
              )}
            </div>
          ))
        )}
        </div>
      </div>

      {/* Paginação */}
      {pagination && filteredData.length > pageSize && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Mostrando {startIndex + 1} a {Math.min(endIndex, filteredData.length)} de {filteredData.length} registro{filteredData.length !== 1 ? 's' : ''}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePreviousPage}
              disabled={currentPage === 1 || loading}
            >
              <ChevronLeft className="h-4 w-4" />
              Anterior
            </Button>
            <div className="text-sm font-medium">
              Página {currentPage} de {totalPages}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleNextPage}
              disabled={currentPage === totalPages || loading}
            >
              Próxima
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
} 